import {DotLottieController, isDotLottieWC} from './DotLottieController';
import {
  intersectionMap,
  intersectionObserver,
  isIntersectionValid,
} from './IntersectionObserver';
import {attributeFilter, nodeAddedMap} from './MutationHandler';
import {
  ensureVimeoLoaded,
  ensureYouTubeApiEnabled,
  ensureYouTubeLoaded,
  isElementVimeo,
  isHTMLElement,
} from './utils';

const attribute = 'data-start-on-intersection';
const startedMap: WeakMap<Element, boolean> = new WeakMap();

export const StartOnIntersectionController = Object.freeze({
  attribute,
  selector: `[${attribute}]`,
});

intersectionMap.set(StartOnIntersectionController.selector, (entry) => {
  const {target} = entry;
  if (
    !target.matches(StartOnIntersectionController.selector) ||
    !isHTMLElement(target) ||
    !isIntersectionValid(entry)
  ) {
    return;
  }

  const started = startedMap.get(target);
  if (started) {
    return;
  }

  if (target.matches('video')) {
    try {
      (<HTMLVideoElement>target).play();
      startedMap.set(target, true);
    } catch {
      // ignore
    }

    return;
  }

  if (isElementVimeo(target)) {
    ensureVimeoLoaded().then((x) => {
      try {
        const player = new Vimeo.Player(target);
        player.play();
        startedMap.set(target, true);
      } catch {
        // ignore
      }
    });

    return;
  }

  if (ensureYouTubeApiEnabled(target)) {
    ensureYouTubeLoaded().then(() => {
      try {
        const player = new YT.Player(target, {
          events: {
            onReady: () => {
              player.playVideo();
            },
          },
        });
        startedMap.set(target, true);
      } catch (ex) {
        console.warn(ex);
      }
    });

    return;
  }

  if (isDotLottieWC(target)) {
    DotLottieController.ensureLoaded().then(() => {
      const player = target.dotLottie;
      if (!player) {
        return;
      }
      const ready = () => {
        player.stop();
        player.play();
        player.unfreeze();
        player.removeEventListener('load', ready);
        startedMap.set(target, true);
      };
      if (player.isLoaded) {
        ready();
        return;
      }
      player.addEventListener('load', ready);
    });
  }
});

nodeAddedMap.set(StartOnIntersectionController.selector, (target) => {
  if (!target.matches(StartOnIntersectionController.selector)) {
    return;
  }

  intersectionObserver.observe(target);
});

attributeFilter.add(attribute);
